import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import rehypeRaw from 'rehype-raw';
import Footer from '../../components/feature/Footer';
import ScrollToTop from '../../components/feature/ScrollToTop';
import LoadingSpinner from '../../components/feature/LoadingSpinner';
import BackToHome from '../../components/feature/BackToHome';
import RevealStagger from '../../components/feature/RevealStagger';
import SectionHeader from '../../components/feature/SectionHeader';
import {
  fetchAboutHero,
  fetchAboutPhilosophy,
  fetchAboutTwoWorlds,
  fetchAboutValues,
  fetchAboutJourney,
  fetchAboutExpertise,
  fetchAboutQualifications,
  type AboutHero,
  type AboutPhilosophy,
  type AboutTwoWorlds,
  type AboutValues,
  type AboutJourney,
  type AboutExpertise,
  type AboutQualifications,
} from '../../services/aboutPage';
import { fetchContactSection, type ContactSection } from '../../services/contactSection';
import Seo, { buildBreadcrumbJsonLd } from '../../components/feature/Seo';
import { PERSON_JSONLD } from '../../seo/siteMetadata';

export default function About() {
  const [hero, setHero] = useState<AboutHero | null>(null);
  const [philosophy, setPhilosophy] = useState<AboutPhilosophy | null>(null);
  const [twoWorlds, setTwoWorlds] = useState<AboutTwoWorlds | null>(null);
  const [values, setValues] = useState<AboutValues | null>(null);
  const [journey, setJourney] = useState<AboutJourney | null>(null);
  const [expertise, setExpertise] = useState<AboutExpertise | null>(null);
  const [qualifications, setQualifications] = useState<AboutQualifications | null>(null);
  const [contactSection, setContactSection] = useState<ContactSection | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [heroData, philosophyData, twoWorldsData, valuesData, journeyData, expertiseData, qualificationsData, contactData] = await Promise.all([
          fetchAboutHero(),
          fetchAboutPhilosophy(),
          fetchAboutTwoWorlds(),
          fetchAboutValues(),
          fetchAboutJourney(),
          fetchAboutExpertise(),
          fetchAboutQualifications(),
          fetchContactSection(),
        ]);

        setHero(heroData);
        setPhilosophy(philosophyData);
        setTwoWorlds(twoWorldsData);
        setValues(valuesData);
        setJourney(journeyData);
        setExpertise(expertiseData);
        setQualifications(qualificationsData);
        setContactSection(contactData);
      } catch (error) {
        console.error('Failed to load about page:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const seoTitle = hero?.heading
    ? `${hero.heading} | Gerald Fasching`
    : 'Über Gerald Fasching - Business & Personal Coach, Trainer & Mentor';
  const seoDescription =
    'Über Gerald Fasching - Business & Personal Coach, Trainer und Mentor mit über 20 Jahren Führungserfahrung. Verbindet Business, Human und Spirit (BHS-Modell) zu wirksamer Begleitung von Führungskräften, Unternehmer:innen und Privatpersonen.';
  const seo = (
    <Seo
      title={seoTitle}
      description={seoDescription}
      path="/ueber-mich"
      image={hero?.imageUrl}
      jsonLd={[
        PERSON_JSONLD,
        buildBreadcrumbJsonLd([
          { name: 'Start', path: '/' },
          { name: 'Über mich', path: '/ueber-mich' },
        ]),
      ]}
    />
  );

  if (loading) {
    return (
      <>
        {seo}
        <LoadingSpinner bgColor="bg-white" />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {seo}
      {/* Hero Section */}
      {hero && (
        <section id="about-hero" className="relative overflow-hidden px-6 pb-20 pt-32">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-white"></div>
          <div className="max-w-7xl mx-auto relative">
            <BackToHome />
            <div className="grid md:grid-cols-2 gap-16 items-center">
              <RevealStagger animateOnMount>
                <h1 className="text-[1.75rem] md:text-[2.125rem] lg:text-[2.75rem] leading-snug font-bold text-gray-900 mb-6" style={{ fontFamily: 'Lora, serif' }}>
                  {hero.heading}
                </h1>
                <div className="w-20 h-1 bg-gradient-to-r from-red-900 to-amber-600 mb-8"></div>
                {hero.intro && (
                  <div className="text-sm lg:text-xl text-gray-600 leading-relaxed mb-6 [&_p]:m-0 [&_strong]:font-bold [&_strong]:text-gray-900">
                    <ReactMarkdown remarkPlugins={[remarkBreaks]} rehypePlugins={[rehypeRaw]}>{hero.intro}</ReactMarkdown>
                  </div>
                )}
                {hero.description && (
                  <div className="text-base lg:text-lg text-gray-900 leading-relaxed mb-6 [&_p]:m-0 [&_strong]:font-bold [&_strong]:text-[#8B1538]" style={{ fontFamily: 'Lora, serif' }}>
                    <ReactMarkdown remarkPlugins={[remarkBreaks]} rehypePlugins={[rehypeRaw]}>{hero.description}</ReactMarkdown>
                  </div>
                )}
                {hero.subtext && (
                  <div className="text-sm lg:text-base text-gray-600 leading-relaxed [&_p]:m-0 [&_strong]:font-semibold [&_strong]:text-gray-900">
                    <ReactMarkdown remarkPlugins={[remarkBreaks]} rehypePlugins={[rehypeRaw]}>{hero.subtext}</ReactMarkdown>
                  </div>
                )}
              </RevealStagger>
              
              {hero.imageUrl && (
                <RevealStagger animateOnMount className="relative">
                  <div className="aspect-square rounded-2xl overflow-hidden shadow-2xl">
                    <img
                      src={hero.imageUrl}
                      alt={`Gerald Fasching - ${hero.heading || 'Business Coach, Trainer und Mentor'}`}
                      loading="eager"
                      fetchPriority="high"
                      decoding="async"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-gradient-to-br from-red-900/10 to-amber-600/10 rounded-2xl -z-10"></div>
                  <div className="absolute -top-6 -left-6 w-32 h-32 bg-gradient-to-br from-[#C9A961]/10 to-[#C9A961]/5 rounded-2xl -z-10"></div>
                </RevealStagger>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Philosophy Section - editorial manifesto */}
      {philosophy && (
        <section id="philosophie" className="relative overflow-hidden bg-[#1A365D] px-6 py-14 text-white sm:py-16 lg:py-20">
          <div className="pointer-events-none absolute -left-32 top-0 h-[32rem] w-[32rem] rounded-full bg-[#C9A961]/[0.08] blur-3xl" />
          <div className="pointer-events-none absolute -right-32 bottom-0 h-[32rem] w-[32rem] rounded-full bg-[#8B1538]/[0.09] blur-3xl" />
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.05]"
            style={{
              backgroundImage: 'radial-gradient(circle at 2px 2px, #C9A961 1px, transparent 0)',
              backgroundSize: '40px 40px',
            }}
          />

          <RevealStagger animateOnMount className="relative mx-auto max-w-4xl">
            {/* Header */}
            <div className="flex flex-col items-center text-center">
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#C9A961]/30 bg-[#C9A961]/10 px-4 py-2 backdrop-blur-sm">
                <i className="ri-quill-pen-line flex h-4 w-4 items-center justify-center text-[#C9A961]"></i>
                <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#C9A961] lg:text-[13px]">Philosophie</span>
              </div>
              <h2
                className="mb-6 font-serif text-[1.625rem] font-bold leading-snug text-white md:text-[1.875rem] lg:text-[2.5rem]"
                style={{ fontFamily: 'Lora, serif' }}
              >
                {philosophy.heading}
              </h2>
              {/* Ornamental divider: gold dot between two short lines */}
              <div className="mb-8 flex items-center gap-3">
                <span className="h-px w-10 bg-[#C9A961]/60" />
                <span className="h-1.5 w-1.5 rotate-45 bg-[#C9A961]" />
                <span className="h-px w-10 bg-[#C9A961]/60" />
              </div>
            </div>

            {/* Manifesto content */}
            {philosophy.secondaryText && (
              <div className="philosophy-content text-center">
                <ReactMarkdown remarkPlugins={[remarkBreaks]} rehypePlugins={[rehypeRaw]}>{philosophy.secondaryText}</ReactMarkdown>
              </div>
            )}

            {/* Tagline */}
            {philosophy.tagline && (
              <div className="mt-10 flex justify-center">
                <div className="inline-flex items-center gap-3 rounded-full border border-[#C9A961]/30 bg-[#C9A961]/10 px-6 py-3 shadow-[0_4px_20px_-4px_rgba(201,169,97,0.25)] backdrop-blur-sm">
                  <i className="ri-infinity-line flex h-5 w-5 items-center justify-center text-xl text-[#C9A961]"></i>
                  <span className="text-sm font-semibold tracking-[0.15em] text-[#C9A961] lg:text-base">{philosophy.tagline}</span>
                </div>
              </div>
            )}
          </RevealStagger>
        </section>
      )}

      {/* Mein Ansatz - Business · Human · Spirit synergy block */}
      {twoWorlds && (
        <section id="mein-ansatz" className="relative overflow-hidden bg-white px-6 py-16 sm:py-20 lg:py-24">
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.04]"
            style={{
              backgroundImage: 'radial-gradient(circle at 2px 2px, #1A2B3C 1px, transparent 0)',
              backgroundSize: '36px 36px',
            }}
          />
          <div className="pointer-events-none absolute -left-40 top-32 hidden h-96 w-96 rounded-full bg-[#8B1538]/[0.05] blur-3xl lg:block" />
          <div className="pointer-events-none absolute -right-40 bottom-32 hidden h-96 w-96 rounded-full bg-[#1A365D]/[0.06] blur-3xl lg:block" />

          <div className="relative mx-auto max-w-6xl">
            {twoWorlds.synergyHeading && (
              <RevealStagger>
                <div className="relative">
                  <div className="mb-10 flex flex-col items-center text-center lg:mb-12">
                    <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#8B1538]/20 bg-white/70 px-4 py-2 backdrop-blur-sm">
                      <i className="ri-infinity-line flex h-4 w-4 items-center justify-center text-[#8B1538]"></i>
                      <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8B1538] lg:text-[13px]">Mein Ansatz</span>
                    </div>
                    <h3 className="mb-5 text-[1.5rem] font-bold leading-snug text-gray-900 md:text-[1.75rem] lg:text-[2rem]" style={{ fontFamily: 'Lora, serif' }}>
                      {twoWorlds.synergyHeading}
                    </h3>
                    <div className="mb-6 h-px w-12 bg-[#C9A961]" />
                  </div>

                  <div className="mb-10 grid grid-cols-1 gap-6 md:grid-cols-3 lg:gap-8" data-reveal-expand="">
                    {[
                      {
                        accent: '#1A365D',
                        title: 'Business',
                        desc: 'Struktur, Fokus, strategische Entscheidungen',
                        icon: 'ri-briefcase-line',
                      },
                      {
                        accent: '#8B1538',
                        title: 'Human',
                        desc: 'Haltung, Beziehung, innere Dynamiken',
                        icon: 'ri-team-line',
                      },
                      {
                        accent: '#C9A961',
                        title: 'Spirit',
                        desc: 'Bewusstsein, Resonanzfelder, mentale & energetische Muster',
                        icon: 'ri-compass-3-line',
                      },
                    ].map((p) => (
                      <article
                        key={p.title}
                        className="group relative flex flex-col overflow-hidden rounded-2xl bg-white p-8 shadow-[0_2px_12px_-2px_rgba(26,43,60,0.08)] transition-[box-shadow,transform] duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_28px_-8px_rgba(26,43,60,0.14)] lg:p-9"
                        style={{ border: `1px solid ${p.accent}1f` }}
                      >
                        <div className="absolute inset-x-0 top-0 h-1" style={{ backgroundColor: p.accent }} />
                        <div
                          className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full opacity-[0.06] blur-3xl transition-opacity duration-500 group-hover:opacity-[0.12]"
                          style={{ backgroundColor: p.accent }}
                        />

                        <div className="relative z-10 flex flex-1 flex-col">
                          <div
                            className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl transition-transform duration-300 group-hover:scale-110"
                            style={{ backgroundColor: `${p.accent}1a` }}
                          >
                            <i className={`${p.icon} text-xl`} style={{ color: p.accent }}></i>
                          </div>
                          <h4 className="mb-3 text-xl font-bold leading-tight text-gray-900 lg:text-[1.35rem]" style={{ fontFamily: 'Lora, serif' }}>
                            {p.title}
                          </h4>
                          <p className="text-sm leading-relaxed text-gray-600 lg:text-base">{p.desc}</p>
                        </div>
                      </article>
                    ))}
                  </div>

                  <div className="flex flex-col items-center gap-8">
                    {twoWorlds.synergySubtext && (
                      <div className="text-center">
                        <div className="mx-auto mb-4 h-px w-12 bg-[#C9A961]" />
                        <p
                          className="mx-auto max-w-2xl text-base italic text-[#1A2B3C]/80 lg:text-lg"
                          style={{ fontFamily: 'Lora, serif' }}
                        >
                          {twoWorlds.synergySubtext}
                        </p>
                      </div>
                    )}
                    <Link
                      to="/bhs-modell"
                      className="group inline-flex items-center justify-center whitespace-nowrap rounded-full bg-[#8B1538] px-7 py-3.5 text-sm font-semibold leading-none text-white shadow-lg shadow-[#8B1538]/25 transition-all hover:bg-[#6B0F2A] hover:shadow-xl active:translate-y-[1px]"
                    >
                      Mehr erfahren
                      <i className="ri-arrow-right-line ml-2 flex h-4 w-4 items-center justify-center transition-transform group-hover:translate-x-1"></i>
                    </Link>
                  </div>
                </div>
              </RevealStagger>
            )}
          </div>
        </section>
      )}

      {/* Values Section */}
      {values && values.values && values.values.length > 0 && (
        <section id="werte" className="px-6 py-16 sm:py-20 lg:py-24">
          <div className="max-w-7xl mx-auto">
            <RevealStagger className="mb-16">
              <SectionHeader
                badgeIcon="ri-heart-3-line"
                badgeLabel="Werte"
                heading={values.heading}
                subtitle={values.subheading || undefined}
              />
            </RevealStagger>

            <RevealStagger className="grid md:grid-cols-2 lg:grid-cols-5 gap-8">
              {values.values.map((value, index) => (
                <div key={index} className="text-center group">
                  <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-red-900 to-red-800 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <i className={`${value.icon} text-3xl text-white`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Lora, serif' }}>
                    {value.title}
                  </h3>
                  <p className="text-sm lg:text-base text-gray-600 leading-relaxed">
                    {value.description}
                  </p>
                </div>
              ))}
            </RevealStagger>
          </div>
        </section>
      )}

      {/* Journey Timeline */}
      {journey && journey.milestones && journey.milestones.length > 0 && (
        <section id="werdegang" className="bg-gray-50 px-6 py-16 sm:py-20 lg:py-24">
          <div className="max-w-5xl mx-auto">
            <RevealStagger className="mb-16">
              <SectionHeader
                badgeIcon="ri-time-line"
                badgeLabel="Werdegang"
                heading={journey.heading}
                subtitle={journey.subheading || undefined}
              />
            </RevealStagger>

            <div className="relative">
              <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gradient-to-b from-red-900 via-amber-600 to-blue-900 hidden lg:block"></div>

              <RevealStagger className="space-y-12">
                {journey.milestones.map((milestone, index) => (
                  <div key={index} className={`flex items-center gap-8 ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}>
                    <div className={`flex-1 ${index % 2 === 0 ? 'lg:text-right' : 'lg:text-left'}`}>
                      <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
                        <div className="text-2xl font-bold text-amber-600 mb-2" style={{ fontFamily: 'Lora, serif' }}>
                          {milestone.year}
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">
                          {milestone.title}
                        </h3>
                        <p className="text-sm lg:text-base text-gray-600">
                          {milestone.description}
                        </p>
                      </div>
                    </div>

                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-red-900 to-amber-600 items-center justify-center flex-shrink-0 shadow-lg z-10 hidden lg:flex">
                      <div className="w-8 h-8 rounded-full bg-white"></div>
                    </div>

                    <div className="flex-1 hidden lg:block"></div>
                  </div>
                ))}
              </RevealStagger>
            </div>
          </div>
        </section>
      )}

      {/* Expertise Section */}
      {expertise && expertise.categories && expertise.categories.length > 0 && (
        <section id="expertise" className="px-6 py-16 sm:py-20 lg:py-24">
          <div className="max-w-7xl mx-auto">
            <RevealStagger className="mb-16">
              <SectionHeader
                badgeIcon="ri-bar-chart-2-line"
                badgeLabel="Expertise"
                heading={expertise.heading}
                subtitle={expertise.subheading || undefined}
              />
            </RevealStagger>

            <RevealStagger className="flex flex-wrap justify-center gap-8">
              {expertise.categories.map((item, index) => (
                <div key={index} className={`w-full rounded-2xl border border-gray-200 bg-white p-8 shadow-[0_2px_12px_-2px_rgba(26,43,60,0.08)] transition-[box-shadow,transform] duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_28px_-8px_rgba(26,43,60,0.14)] ${item.areas && item.areas.length > 0 ? 'md:w-[calc(33.333%-1.34rem)]' : 'md:px-12 md:text-center'}`}>
                  <h3 className={`text-2xl font-bold leading-snug text-gray-900 ${item.areas && item.areas.length > 0 ? 'mb-6' : 'mb-0'}`} style={{ fontFamily: 'Lora, serif' }}>
                    {item.category}
                  </h3>
                  {item.areas && item.areas.length > 0 && (
                    <ul className="space-y-3">
                      {item.areas.map((area, areaIndex) => (
                        <li key={areaIndex} className="flex items-start gap-3">
                          <i className="ri-check-line text-red-900 text-xl flex-shrink-0 mt-0.5"></i>
                          <span className="text-sm lg:text-base text-gray-600">{area.text}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </RevealStagger>
          </div>
        </section>
      )}

      {/* Qualifications Section */}
      {qualifications && qualifications.qualifications && qualifications.qualifications.length > 0 && (
        <section id="qualifikationen" className="bg-[#1A365D] px-6 py-16 text-white sm:py-20 lg:py-24">
          <div className="mx-auto max-w-7xl">
            <RevealStagger className="mb-16">
              <SectionHeader
                variant="dark"
                badgeIcon="ri-medal-line"
                badgeLabel="Qualifikationen"
                heading={qualifications.heading}
                subtitle={qualifications.subheading || undefined}
              />
            </RevealStagger>

            <RevealStagger className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              {qualifications.qualifications.map((qual, index) => (
                <div key={index} className="rounded-xl bg-white/10 p-6 text-center backdrop-blur-sm transition-colors hover:bg-white/20">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-white/20 flex items-center justify-center">
                    <i className={`${qual.icon} text-3xl text-amber-400`}></i>
                  </div>
                  <p className="text-sm font-semibold lg:text-base">{qual.text}</p>
                </div>
              ))}
            </RevealStagger>
          </div>
        </section>
      )}

      {/* Contact Section - same as home page */}
      {contactSection && (
        <section id="about-kontakt" className="bg-[#FAF8F5] px-6 py-20 sm:py-24 lg:px-12 lg:py-32">
          <RevealStagger className="mx-auto flex max-w-4xl flex-col items-center space-y-8 text-center">
            <h2 className="font-serif text-[1.625rem] font-bold leading-snug text-[#1A2B3C] md:text-[1.875rem] lg:text-[2.5rem]" style={{ fontFamily: 'Lora, serif' }}>
              {contactSection.heading}
            </h2>
            {contactSection.description && (
              <p className="max-w-2xl text-base text-gray-700 lg:text-xl">
                {contactSection.description}
              </p>
            )}
            {(contactSection.primaryButtonText && contactSection.primaryButtonLink) ||
            (contactSection.secondaryButtonText && contactSection.secondaryButtonLink) ? (
              <div className="flex flex-col gap-4 pt-8 sm:flex-row sm:justify-center" data-reveal-expand="">
                {contactSection.primaryButtonText && contactSection.primaryButtonLink && (
                  <a href={contactSection.primaryButtonLink} className="inline-flex items-center justify-center whitespace-nowrap rounded-full bg-[#8B1538] px-7 py-3.5 text-sm font-semibold leading-none text-white shadow-lg shadow-[#8B1538]/25 transition-all hover:bg-[#6B0F2A] hover:shadow-xl active:translate-y-[1px]">
                    {contactSection.primaryButtonText}
                  </a>
                )}
                {contactSection.secondaryButtonText && contactSection.secondaryButtonLink && (
                  <a href={contactSection.secondaryButtonLink} className="inline-flex items-center justify-center whitespace-nowrap rounded-full border border-[#1A2B3C]/25 bg-white px-7 py-3.5 text-sm font-semibold leading-none text-[#1A2B3C] transition-all hover:border-[#1A2B3C] hover:bg-[#1A2B3C] hover:text-white active:translate-y-[1px]">
                    {contactSection.secondaryButtonIcon && (
                      <i className={`${contactSection.secondaryButtonIcon} mr-2 flex h-5 w-5 items-center justify-center`}></i>
                    )}
                    {contactSection.secondaryButtonText}
                  </a>
                )}
              </div>
            ) : null}
          </RevealStagger>
        </section>
      )}

      <Footer />
      <ScrollToTop />
    </div>
  );
}
